
import { useCallback } from "react";
import { toast } from "sonner";
import { UseAutoFetchStateReturn } from "./useAutoFetchState";
import { VideoFetchResult } from "@/types/api";

interface UseAutoVideoFetchProps {
  isLoading: boolean;
  isFetchingMore: boolean;
  url: string;
  isOnline: boolean;
  autoFetchState: UseAutoFetchStateReturn;
  setVideos: (videos: any[]) => void;
  setCurrentCursor: (cursor: string) => void;
  fetchVideos: (currentUrl: string, cursor: string, append: boolean) => Promise<VideoFetchResult | null>;
}

export function useAutoVideoFetch({
  isLoading,
  isFetchingMore,
  url,
  isOnline,
  autoFetchState,
  setVideos,
  setCurrentCursor,
  fetchVideos
}: UseAutoVideoFetchProps) {
  const continueAutoFetch = useCallback(async (currentUrl: string, cursor: string) => {
    let currentCursor = cursor;
    let hasMoreVideos = true;
    let consecutiveErrors = 0;
    let lastSuccessfulCursor = cursor;
    const MAX_CONSECUTIVE_ERRORS = 5;
    const MIN_RETRY_DELAY = 250;
    
    while (hasMoreVideos && autoFetchState.shouldContinueRef.current && autoFetchState.autoFetchingRef.current && consecutiveErrors < MAX_CONSECUTIVE_ERRORS) {
      if (autoFetchState.autoFetchPaused) {
        await new Promise(resolve => setTimeout(resolve, 100));
        continue;
      }
      
      try {
        const result = await fetchVideos(currentUrl, currentCursor, true);
        
        if (result?.success) {
          consecutiveErrors = 0;
          hasMoreVideos = result.hasMore;
          lastSuccessfulCursor = currentCursor;
          currentCursor = result.nextCursor;
          
          if (!hasMoreVideos) {
            toast.success(`Collection fetch complete - Total videos: ${autoFetchState.totalVideosFetched}`);
            break;
          }
        } else {
          consecutiveErrors++;
          const retryDelay = MIN_RETRY_DELAY * Math.pow(1.5, consecutiveErrors - 1);
          
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            autoFetchState.setFetchErrored(true);
            toast.error(`Auto-fetch stopped after ${autoFetchState.totalVideosFetched} videos - Too many errors. Last successful cursor: ${lastSuccessfulCursor}`);
            break;
          }
          
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      } catch (error) {
        console.error('Error in auto-fetch:', error);
        consecutiveErrors++;
        const retryDelay = MIN_RETRY_DELAY * Math.pow(1.5, consecutiveErrors - 1);
        
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          autoFetchState.setFetchErrored(true);
          toast.error(`Auto-fetch stopped after ${autoFetchState.totalVideosFetched} videos - Too many errors. Last successful cursor: ${lastSuccessfulCursor}`);
          break;
        }
        
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
    
    autoFetchState.setIsAutoFetching(false);
  }, [autoFetchState, fetchVideos]);

  const startAutoFetch = useCallback(async () => {
    if (isLoading || isFetchingMore || !url.trim() || !isOnline || autoFetchState.fetchErrored) {
      // Don't auto-fetch if we've already encountered an error with this URL
      if (autoFetchState.fetchErrored && autoFetchState.lastFetchedUrl === url) {
        return;
      }
      return;
    }
    
    autoFetchState.setFetchedPagesCount(0);
    autoFetchState.setTotalVideosFetched(0);
    autoFetchState.setAutoFetchPaused(false);
    autoFetchState.setFetchErrored(false);
    autoFetchState.shouldContinueRef.current = true;
    autoFetchState.setLastFetchedUrl(url);
    
    autoFetchState.setIsAutoFetching(true);
    setVideos([]); 
    setCurrentCursor("0");
    
    try {
      const result = await fetchVideos(url, "0", false);
      
      if (result?.success && result.hasMore) {
        await continueAutoFetch(url, result.nextCursor);
      } else {
        autoFetchState.setIsAutoFetching(false);
        if (result?.success) {
          toast.success(`Collection fetch complete - Total videos: ${autoFetchState.totalVideosFetched}`);
        } else {
          autoFetchState.setFetchErrored(true);
        }
      }
    } catch (error) {
      console.error("Error starting auto-fetch:", error);
      autoFetchState.setIsAutoFetching(false);
      autoFetchState.setFetchErrored(true);
    }
  }, [
    url,
    isLoading,
    isFetchingMore,
    isOnline,
    fetchVideos,
    continueAutoFetch,
    autoFetchState,
    setVideos,
    setCurrentCursor
  ]);

  return {
    startAutoFetch,
    continueAutoFetch
  };
}
